import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  ArrowRight, 
  Brain, 
  Code, 
  Smartphone, 
  Link2, 
  CheckCircle2,
  Cpu,
  Database,
  Cloud,
  Workflow,
  Layers,
  Globe,
  Palette,
  Server,
  Shield,
  Zap,
  BarChart3,
  Settings,
  ShoppingCart,
  Briefcase,
  Users,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export const Services = () => {
  const location = useLocation();

  // Scroll to service section when hash changes
  useEffect(() => {
    const hash = location.hash.substring(1); // Remove the '#'
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const mainServices = [
    {
      id: 'ai-integration',
      icon: Brain,
      title: 'AI Integration',
      subtitle: 'Intelligent Solutions for Smart Businesses',
      description: 'Transform your operations with cutting-edge AI technology. We integrate machine learning, natural language processing, and predictive analytics into your existing systems.',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'from-blue-50 to-indigo-50',
      features: [
        'Machine Learning Model Integration',
        'Natural Language Processing (NLP)',
        'Computer Vision Solutions',
        'Predictive Analytics',
        'AI-Powered Automation',
        'Custom AI Model Development'
      ],
      subServices: [
        { icon: Cpu, title: 'ML Models', desc: 'Custom machine learning solutions' },
        { icon: BarChart3, title: 'Analytics', desc: 'Predictive business analytics' },
        { icon: Workflow, title: 'Automation', desc: 'AI-driven process automation' },
      ]
    },
    {
      id: 'integrations',
      icon: Link2,
      title: 'Custom Integrations',
      subtitle: 'Connect Your Digital Ecosystem',
      description: 'Seamlessly connect your systems with custom API integrations. We specialize in complex system integrations that streamline your operations.',
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'from-teal-50 to-cyan-50',
      features: [
        'API Development & Integration',
        'ERP System Integration',
        'CRM Integrations',
        'Payment Gateway Integration',
        'Third-Party API Connections',
        'Data Migration & Sync'
      ],
      subServices: [
        { icon: Database, title: 'Data Sync', desc: 'Real-time data synchronization' },
        { icon: Cloud, title: 'Cloud', desc: 'Cloud service integration' },
        { icon: Settings, title: 'Custom APIs', desc: 'Tailored API solutions' },
      ]
    },
    {
      id: 'shopify',
      icon: ShoppingCart,
      title: 'Shopify Solutions',
      subtitle: 'E-Commerce Excellence',
      description: 'Build powerful, scalable e-commerce platforms on Shopify. From store setup to advanced customization, we create shopping experiences that convert.',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'from-green-50 to-emerald-50',
      features: [
        'Shopify Store Development',
        'Theme Customization & Design',
        'App Integration & Development',
        'Payment Gateway Setup',
        'Inventory Management Solutions',
        'Performance & SEO Optimization'
      ],
      subServices: [
        { icon: ShoppingCart, title: 'Store Setup', desc: 'Complete Shopify store configuration' },
        { icon: Palette, title: 'Design', desc: 'Custom themes and UI design' },
        { icon: Zap, title: 'Performance', desc: 'Speed and conversion optimization' },
      ]
    },
    {
      id: 'netsuite',
      icon: Briefcase,
      title: 'NetSuite Integration',
      subtitle: 'Enterprise Resource Planning',
      description: 'Maximize your NetSuite investment with expert implementation and integration. We help you streamline operations, automate processes, and gain actionable insights.',
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'from-indigo-50 to-purple-50',
      features: [
        'NetSuite Implementation',
        'Custom Module Development',
        'ERP Integration & Migration',
        'Business Process Automation',
        'Financial & Operational Analytics',
        'Training & Change Management'
      ],
      subServices: [
        { icon: Server, title: 'Implementation', desc: 'Full-scale NetSuite setup' },
        { icon: BarChart3, title: 'Analytics', desc: 'Business intelligence & reporting' },
        { icon: Workflow, title: 'Automation', desc: 'Process automation & workflows' },
      ]
    },
    {
      id: 'web-development',
      icon: Code,
      title: 'Web Development',
      subtitle: 'Modern Web Solutions That Scale',
      description: 'Build stunning, high-performance websites and web applications. From corporate sites to complex platforms, we deliver exceptional digital experiences.',
      color: 'from-purple-500 to-pink-600',
      bgColor: 'from-purple-50 to-pink-50',
      features: [
        'Custom Web Application Development',
        'E-commerce Solutions',
        'Progressive Web Apps (PWA)',
        'API Development & Integration',
        'Performance Optimization',
        'Cloud-Native Architecture'
      ],
      subServices: [
        { icon: Globe, title: 'Websites', desc: 'Responsive modern websites' },
        { icon: Palette, title: 'UI/UX', desc: 'Beautiful user interfaces' },
        { icon: Server, title: 'Backend', desc: 'Scalable server solutions' },
      ]
    },
    {
      id: 'app-development',
      icon: Smartphone,
      title: 'App Development',
      subtitle: 'Mobile Apps That Engage',
      description: 'Create powerful mobile applications for iOS and Android. We build native and cross-platform apps that deliver seamless user experiences.',
      color: 'from-orange-500 to-red-600',
      bgColor: 'from-orange-50 to-red-50',
      features: [
        'iOS App Development',
        'Android App Development',
        'Cross-Platform Solutions',
        'App UI/UX Design',
        'App Store Optimization',
        'Maintenance & Support'
      ],
      subServices: [
        { icon: Smartphone, title: 'Native Apps', desc: 'iOS & Android native' },
        { icon: Layers, title: 'Cross-Platform', desc: 'React Native & Flutter' },
        { icon: Shield, title: 'Security', desc: 'Secure mobile solutions' },
      ]
    },
    {
      id: 'it-services',
      icon: Server,
      title: 'IT Services & Support',
      subtitle: 'Comprehensive Infrastructure & Operations',
      description: 'Manage your complete IT infrastructure with our comprehensive services. From server management to security operations, we provide 24/7 monitoring and support.',
      color: 'from-red-500 to-orange-600',
      bgColor: 'from-red-50 to-orange-50',
      features: [
        'Server & Infrastructure Management',
        'Cloud Infrastructure Setup & Optimization',
        'Security Operations Center (SOC)',
        'Network Operations Center (NOC)',
        'IT Staffing & Resource Planning',
        '24/7 Monitoring & Technical Support'
      ],
      subServices: [
        { icon: Server, title: 'Server Management', desc: 'Reliable server administration' },
        { icon: Cloud, title: 'Cloud Infra', desc: 'AWS, Azure, GCP management' },
        { icon: Shield, title: 'SOC/NOC', desc: 'Security & network operations' },
      ]
    },
    {
      id: 'erp-solutions',
      icon: Database,
      title: 'ERP Solutions',
      subtitle: 'Enterprise Resource Planning Systems',
      description: 'Deploy comprehensive enterprise resource planning systems that unify your business processes. We implement, customize, and integrate ERP solutions for seamless operations.',
      color: 'from-rose-500 to-pink-600',
      bgColor: 'from-rose-50 to-pink-50',
      features: [
        'ERP System Implementation',
        'Custom Module Development',
        'Business Process Optimization',
        'Data Migration & Integration',
        'Financial & Operational Analytics',
        'User Training & Support'
      ],
      subServices: [
        { icon: Database, title: 'Implementation', desc: 'Full ERP system deployment' },
        { icon: BarChart3, title: 'Analytics', desc: 'Business intelligence tools' },
        { icon: Workflow, title: 'Automation', desc: 'Workflow & process automation' },
      ]
    },
    {
      id: 'cloud-infrastructure',
      icon: Cloud,
      title: 'Cloud Infrastructure',
      subtitle: 'Scalable Cloud Solutions',
      description: 'Build and manage scalable cloud infrastructure using AWS, Azure, and Google Cloud. We design, implement, and optimize cloud solutions for performance and cost-efficiency.',
      color: 'from-indigo-500 to-blue-600',
      bgColor: 'from-indigo-50 to-blue-50',
      features: [
        'AWS Cloud Architecture',
        'Azure Infrastructure Setup',
        'Google Cloud Solutions',
        'Kubernetes & Container Orchestration',
        'Cloud Migration & Optimization',
        'Infrastructure as Code (IaC)'
      ],
      subServices: [
        { icon: Cloud, title: 'AWS', desc: 'Amazon Web Services solutions' },
        { icon: Server, title: 'Kubernetes', desc: 'Container orchestration' },
        { icon: Zap, title: 'Optimization', desc: 'Performance tuning' },
      ]
    },
    {
      id: 'cybersecurity',
      icon: Shield,
      title: 'Cybersecurity Solutions',
      subtitle: 'Protect Your Digital Assets',
      description: 'Protect your business from cyber threats with comprehensive security solutions. We provide threat assessment, compliance management, and 24/7 monitoring.',
      color: 'from-red-500 to-rose-600',
      bgColor: 'from-red-50 to-rose-50',
      features: [
        'Security Threat Assessment',
        'Vulnerability Management',
        'Compliance & Regulatory Support',
        'Data Protection & Encryption',
        'Security Operations Monitoring',
        'Incident Response & Recovery'
      ],
      subServices: [
        { icon: Shield, title: 'Assessment', desc: 'Security audits & assessments' },
        { icon: AlertCircle, title: 'Monitoring', desc: '24/7 threat monitoring' },
        { icon: Server, title: 'Compliance', desc: 'Regulatory compliance' },
      ]
    }
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity. Simple websites can be completed in 4-6 weeks, while complex AI integrations may take 3-6 months. We provide detailed timelines during our initial consultation.'
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes! We offer comprehensive maintenance and support packages. This includes regular updates, bug fixes, performance monitoring, and technical support to ensure your solution runs smoothly.'
    },
    {
      question: 'What technologies do you work with?',
      answer: 'We work with a wide range of modern technologies including React, Node.js, Python, TensorFlow, AWS, Google Cloud, and more. We select the best stack based on your specific requirements.'
    },
    {
      question: 'How do you ensure project quality?',
      answer: 'We follow agile methodologies with regular code reviews, automated testing, and quality assurance processes. You receive regular updates and can track progress through our project management tools.'
    },
    {
      question: 'Can you integrate with our existing systems?',
      answer: 'Absolutely! Integration is one of our specialties. We have extensive experience connecting various platforms, ERPs, CRMs, and custom systems. Our Setu AI product is a perfect example of our integration expertise.'
    },
  ];

  return (
    <div data-testid="services-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 hero-gradient mesh-gradient">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Our Services
            </span>
            <h1 
              data-testid="services-hero-title"
              className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6"
            >
              Technology Solutions for{' '}
              <span className="gradient-text">Tomorrow's Leaders</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              From AI integration to custom development, we offer comprehensive technology services 
              designed to transform your business and drive sustainable growth.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      {mainServices.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                  {service.subtitle}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mt-2 mb-4">
                  {service.title}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {service.description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to="/contact">
                  <Button 
                    data-testid={`${service.id}-cta`}
                    className={`bg-gradient-to-r ${service.color} hover:opacity-90 text-white rounded-full px-8 py-6 font-semibold shadow-lg group`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              {/* Visual Card */}
              <div className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                <Card className={`bg-gradient-to-br ${service.bgColor} border-0 rounded-3xl overflow-hidden shadow-xl`}>
                  <CardContent className="p-8">
                    <div className="grid gap-6">
                      {service.subServices.map((sub, i) => (
                        <div 
                          key={i}
                          className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                            <sub.icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{sub.title}</h4>
                            <p className="text-sm text-slate-500">{sub.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Decorative Stats */}
                    <div className="mt-8 pt-8 border-t border-slate-200/50">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold font-['Outfit'] text-slate-900">50+</p>
                          <p className="text-xs text-slate-500">Projects</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold font-['Outfit'] text-slate-900">99%</p>
                          <p className="text-xs text-slate-500">Satisfaction</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold font-['Outfit'] text-slate-900">24/7</p>
                          <p className="text-xs text-slate-500">Support</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-blue-400 text-sm font-medium mb-4">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white">
              How We Work
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'We understand your needs, goals, and challenges.' },
              { step: '02', title: 'Strategy', desc: 'We design a tailored solution roadmap.' },
              { step: '03', title: 'Development', desc: 'We build with agile methodology and regular updates.' },
              { step: '04', title: 'Launch & Support', desc: 'We deploy and provide ongoing maintenance.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white font-['Outfit']">{item.step}</span>
                </div>
                <h4 className="text-xl font-bold font-['Outfit'] text-white mb-2">{item.title}</h4>
                <p className="text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-section" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="bg-slate-50 rounded-2xl px-6 border-0"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 cta-gradient relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white mb-6">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss your project and find the perfect solution for your business.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="services-cta-button"
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl group"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
